import { faFileContract, faPencil } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { Label } from '@shared/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { useCountryTranslation } from '@shared/hooks/useCountryTranslation';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUsers } from '../../../CreateEdit/hooks';
import { ErrorState, LoadingState } from '../../../List/components/feedback';
import { useStartImpersonation } from '../../../List/hooks/useImpersonate';
import { useClient, useUpdateCRMSettings } from '../../hooks';
import { useTeamContacts } from '../../hooks/useTeamContacts';
import {
  BankAccountsTab,
  Breadcrumb,
  ContactsTab,
  EditBillingModal,
  EditCommercialModal,
  HeaderButtons,
  InvoicesTab,
  MigrationTab,
  RateAssignmentsTab,
  ShopsTab,
} from '../display';

export const Main = () => {
  const { t, i18n } = useTranslation('viewClientModule');
  const { canAccess } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const clientId = id ? parseInt(id, 10) : 0;
  const { getCountryName } = useCountryTranslation();

  const { client, isLoading, error, refetch } = useClient(clientId);
  const { mutate: impersonate } = useStartImpersonation();
  const { users } = useUsers();
  const { contacts: apiContacts, refetch: refetchContacts } = useTeamContacts(clientId);
  const { toast } = useToast();
  const updateCRMSettingsMutation = useUpdateCRMSettings();
  const [isProcessingCRM, setIsProcessingCRM] = useState(false);
  const crmChangeModalRef = useRef<ConfirmModalRef>(null);
  const crmEnabledChangeModalRef = useRef<ConfirmModalRef>(null);
  const [, setPendingCRMValue] = useState<'tenant_external' | 'tenant_3pl' | null>(null);
  const [, setPendingCRMEnabled] = useState<boolean | null>(null);

  const [isEditBillingModalOpen, setIsEditBillingModalOpen] = useState(false);
  const [isEditCommercialModalOpen, setIsEditCommercialModalOpen] = useState(false);
  const canEditBilling = canAccess(['tenant_3pl'], ['iam.teams.edit-child-billing']);
  const canEditCommercial = canAccess(['tenant_3pl'], ['iam.teams.edit-child-commercial']);

  // Obtener el nombre del comercial asignado
  const getCommercialName = (commercialId: number | null | undefined) => {
    if (!commercialId) return null;
    const commercial = users.find((u) => u.id === commercialId);
    return commercial?.name || null;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('placeholders.noData');
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'PPp', { locale });
    } catch {
      return t('placeholders.noData');
    }
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return t('placeholders.noData');
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'dd/MM/yyyy', { locale });
    } catch {
      return t('placeholders.noData');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Helper para obtener el valor del setting crm.managed_by
  const getCRMManagedBySetting = (): 'tenant_external' | 'tenant_3pl' | null => {
    if (!client?.settings) return null;
    const crmSetting = client.settings.find((s) => s.name === 'crm.managed_by');
    const value = crmSetting?.value?.value;
    if (value === 'tenant_external' || value === 'tenant_3pl') {
      return value;
    }
    return null;
  };

  // Helper para obtener el valor del setting crm.enabled
  const getCRMEnabledSetting = (): boolean => {
    if (!client?.settings) return false;
    const crmEnabledSetting = client.settings.find((s) => s.name === 'crm.enabled');
    const value = crmEnabledSetting?.value?.value;
    return value === true || value === 'true';
  };

  const currentCRMValue = getCRMManagedBySetting();
  const currentCRMEnabled = getCRMEnabledSetting();
  const [localCRMValue, setLocalCRMValue] = useState<'tenant_external' | 'tenant_3pl' | null>(currentCRMValue);
  const [localCRMEnabled, setLocalCRMEnabled] = useState<boolean>(currentCRMEnabled);

  // Sincronizar los valores locales con los valores del cliente cuando cambian
  useEffect(() => {
    setLocalCRMValue(currentCRMValue);
  }, [currentCRMValue]);

  useEffect(() => {
    setLocalCRMEnabled(currentCRMEnabled);
  }, [currentCRMEnabled]);

  // Handler para cambiar la gestión de CRM
  const handleCRMChange = (newValue: 'tenant_external' | 'tenant_3pl') => {
    // Si el valor es el mismo, no hacer nada
    if (currentCRMValue === newValue) return;

    // Guardar el valor pendiente y mostrar modal de confirmación
    setPendingCRMValue(newValue);
    crmChangeModalRef.current?.open(
      async () => {
        setIsProcessingCRM(true);
        try {
          await updateCRMSettingsMutation.mutateAsync({
            setting_key: 'crm.managed_by',
            value: newValue,
            team_id: clientId,
          });
          setLocalCRMValue(newValue);
          toast({
            title: t('crmManagement.success.updated.title'),
            description: t('crmManagement.success.updated.description'),
            variant: 'default',
          });
          refetch();
        } catch (error: any) {
          toast({
            title: t('crmManagement.error.title'),
            description: error?.response?.data?.message ?? t('crmManagement.error.description'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessingCRM(false);
          setPendingCRMValue(null);
        }
      },
      () => {
        // Cancelar: mantener el valor actual y limpiar el valor pendiente
        setLocalCRMValue(currentCRMValue);
        setPendingCRMValue(null);
      },
      t('crmManagement.confirmChange.title'),
      t('crmManagement.confirmChange.message'),
    );
  };

  // Handler para cambiar el estado de activación de CRM
  const handleCRMEnabledChange = (newValue: boolean) => {
    // Si el valor es el mismo, no hacer nada
    if (currentCRMEnabled === newValue) return;

    // Guardar el valor pendiente y mostrar modal de confirmación
    setPendingCRMEnabled(newValue);
    crmEnabledChangeModalRef.current?.open(
      async () => {
        setIsProcessingCRM(true);
        try {
          await updateCRMSettingsMutation.mutateAsync({
            setting_key: 'crm.enabled',
            value: newValue,
            team_id: clientId,
          });
          setLocalCRMEnabled(newValue);
          toast({
            title: t('crmManagement.success.enabledUpdated.title'),
            description: t('crmManagement.success.enabledUpdated.description'),
            variant: 'default',
          });
          refetch();
        } catch (error: any) {
          toast({
            title: t('crmManagement.error.title'),
            description: error?.response?.data?.message ?? t('crmManagement.error.description'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessingCRM(false);
          setPendingCRMEnabled(null);
        }
      },
      () => {
        // Cancelar: mantener el valor actual y limpiar el valor pendiente
        setLocalCRMEnabled(currentCRMEnabled);
        setPendingCRMEnabled(null);
      },
      newValue
        ? t('crmManagement.confirmEnabledChange.enable.title')
        : t('crmManagement.confirmEnabledChange.disable.title'),
      newValue
        ? t('crmManagement.confirmEnabledChange.enable.message')
        : t('crmManagement.confirmEnabledChange.disable.message'),
    );
  };

  if (isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    const errorObj = error instanceof Error ? error : new Error(t('error.generic'));
    return <ErrorState error={errorObj} onRetry={refetch} />;
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-2">{t('error.notFound')}</h2>
          <p className="text-muted-foreground">{t('error.generic')}</p>
        </div>
      </div>
    );
  }

  const beeping_team = client.beeping_team;

  // Usar contactos de la API si están disponibles, sino usar los del cliente como fallback
  const contacts =
    apiContacts.length > 0
      ? apiContacts
      : (() => {
          // Combinar contactos - beeping_team_contact puede ser un objeto o un array
          const contactFromSingular = client.beeping_team_contact
            ? Array.isArray(client.beeping_team_contact)
              ? client.beeping_team_contact
              : [client.beeping_team_contact]
            : [];

          return [...(client.beeping_team_contacts || []), ...contactFromSingular].filter(
            (contact, index, self) =>
              // Eliminar duplicados por id o por nombre si no hay id
              index === self.findIndex((c) => (c.id && contact.id ? c.id === contact.id : c.name === contact.name)),
          );
        })();

  return (
    <div>
      <Breadcrumb clientName={client.name} />
      <HeaderButtons client={client} onImpersonate={impersonate} />

      <div className="px-6 pt-4 pb-6">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="general">{t('tabs.general')}</TabsTrigger>
            <TabsTrigger value="contacts">{t('tabs.contacts.title')}</TabsTrigger>
            <TabsTrigger value="commercial">{t('tabs.commercial.title')}</TabsTrigger>
            <TabsTrigger value="billing">{t('tabs.billing.title')}</TabsTrigger>
            <TabsTrigger value="contracts">{t('tabs.contracts.title')}</TabsTrigger>
            <TabsTrigger value="migration">{t('tabs.migration.title')}</TabsTrigger>
          </TabsList>

          {/* Tab 1: General - Información de la Empresa + Usuario */}
          <TabsContent value="general">
            {/* Información de la Empresa */}
            <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
              <div className="col-span-1 md:col-span-2">
                <h2 className="font-semibold text-lg mb-1">{t('sections.company')}</h2>
                <p className="text-sm text-muted-foreground">{t('sections.companyDescription')}</p>
              </div>
              <div className="col-span-1 md:col-span-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.name')}</p>
                    <p className="text-sm text-muted-foreground">{client.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.vat')}</p>
                    <p className="text-sm text-muted-foreground">{client.vat}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.address')}</p>
                    <p className="text-sm text-muted-foreground">{client.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.city')}</p>
                    <p className="text-sm text-muted-foreground">{client.city}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.region')}</p>
                    <p className="text-sm text-muted-foreground">{client.region}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.zip')}</p>
                    <p className="text-sm text-muted-foreground">{client.zip}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.country')}</p>
                    <p className="text-sm text-muted-foreground">
                      {getCountryName(client.country) || t('placeholders.noData')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Información del Usuario */}
            <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4">
              <div className="col-span-1 md:col-span-2">
                <h2 className="font-semibold text-lg mb-1">{t('sections.user')}</h2>
                <p className="text-sm text-muted-foreground">{t('sections.userDescription')}</p>
              </div>
              <div className="col-span-1 md:col-span-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.userName')}</p>
                    <p className="text-sm text-muted-foreground">{client.user.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.userEmail')}</p>
                    <a href={`mailto:${client.user.email}`} className="text-sm text-primary hover:underline">
                      {client.user.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.userPhone')}</p>
                    {client.user.phone ? (
                      <a href={`tel:${client.user.phone}`} className="text-sm text-primary hover:underline">
                        {client.user.phone}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('placeholders.noData')}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">{t('fields.lastLogin')}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(client.user.last_login_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Contactos */}
          <TabsContent value="contacts">
            <ContactsTab
              contacts={contacts}
              teamId={clientId}
              onRefresh={() => {
                refetchContacts();
                refetch();
              }}
            />
          </TabsContent>

          {/* Tab 3: Comercial - Info comercial + Tarifas + Tiendas */}
          <TabsContent value="commercial">
            {/* Información Comercial */}
            {beeping_team && (
              <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
                <div className="col-span-1 md:col-span-2">
                  <h2 className="font-semibold text-lg mb-1">{t('sections.commercial')}</h2>
                  <p className="text-sm text-muted-foreground">{t('sections.commercialDescription')}</p>
                </div>
                <div className="col-span-1 md:col-span-4">
                  {canEditCommercial && (
                    <div className="flex justify-end mb-4">
                      <Button variant="outline" size="sm" onClick={() => setIsEditCommercialModalOpen(true)}>
                        <FontAwesomeIcon icon={faPencil} className="w-4 h-4 mr-2" />
                        {t('editCommercial.button')}
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.commercialAssigned')}</p>
                      <p className="text-sm text-muted-foreground">
                        {getCommercialName(beeping_team.commercial_id) || t('placeholders.noCommercial')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.firstEntryDate')}</p>
                      <p className="text-sm text-muted-foreground">{formatDateOnly(beeping_team.first_entry_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.companyType')}</p>
                      {beeping_team.company_type ? (
                        <Badge variant={beeping_team.company_type === 'sl' ? 'default' : 'secondary'}>
                          {t(`companyTypes.${beeping_team.company_type}`)}
                        </Badge>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('placeholders.noData')}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.customerOrigin')}</p>
                      {beeping_team.customer_origin ? (
                        <Badge variant="outline">{t(`customerOrigins.${beeping_team.customer_origin}`)}</Badge>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('placeholders.noData')}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.orderType')}</p>
                      {beeping_team.order_type ? (
                        <Badge
                          variant={
                            beeping_team.order_type === 'cod'
                              ? 'default'
                              : beeping_team.order_type === 'card'
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {t(`orderTypes.${beeping_team.order_type}`)}
                        </Badge>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('placeholders.noData')}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.monthlyOrderEstimate')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.monthly_order_estimate !== null &&
                        beeping_team.monthly_order_estimate !== undefined
                          ? `${beeping_team.monthly_order_estimate} ${t('fields.ordersPerMonth')}`
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.storeUrl')}</p>
                      {beeping_team.store_url ? (
                        <a
                          href={beeping_team.store_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          {beeping_team.store_url}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('placeholders.noData')}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.orderAttachmentsEnabled')}</p>
                      <Badge variant={beeping_team.order_attachments_enabled ? 'default' : 'secondary'}>
                        {beeping_team.order_attachments_enabled
                          ? t('booleanValues.enabled')
                          : t('booleanValues.disabled')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.marketingLang')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.marketing_lang ?? t('placeholders.noData')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de edición de comercial */}
            {beeping_team && (
              <EditCommercialModal
                open={isEditCommercialModalOpen}
                onOpenChange={setIsEditCommercialModalOpen}
                teamId={clientId}
                initialCommercialId={beeping_team.commercial_id}
                initialCustomerOrigin={beeping_team.customer_origin ?? null}
                initialMarketingLang={beeping_team.marketing_lang ?? null}
                initialBeepingTeams={beeping_team}
                initialBeepingTeamContacts={(() => {
                  const contactFromSingular = client.beeping_team_contact
                    ? Array.isArray(client.beeping_team_contact)
                      ? client.beeping_team_contact
                      : [client.beeping_team_contact]
                    : [];
                  return [...(client.beeping_team_contacts || []), ...contactFromSingular];
                })()}
                onSuccess={() => refetch()}
              />
            )}

            {/* Gestión de CRM */}
            <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
              <div className="col-span-1 md:col-span-2">
                <h2 className="font-semibold text-lg mb-1">{t('sections.crmManagement')}</h2>
                <p className="text-sm text-muted-foreground">{t('sections.crmManagementDescription')}</p>
              </div>
              <div className="col-span-1 md:col-span-4">
                <div className="space-y-4">
                  {/* Switch para activar/desactivar CRM */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium mb-1">{t('fields.crmEnabled')}</p>
                        <p className="text-xs text-muted-foreground">{t('fields.crmEnabledDescription')}</p>
                      </div>
                      <Switch
                        checked={localCRMEnabled}
                        onCheckedChange={handleCRMEnabledChange}
                        disabled={isProcessingCRM || updateCRMSettingsMutation.isPending}
                      />
                    </div>
                    {/* Modal de confirmación para cambio de estado de CRM */}
                    <ConfirmModal ref={crmEnabledChangeModalRef} />
                  </div>

                  {/* Radio buttons para gestión de CRM (solo si está habilitado) */}
                  {localCRMEnabled && (
                    <div>
                      <p className="text-sm font-medium mb-3">{t('fields.crmManagedBy')}</p>
                      <RadioGroup
                        value={localCRMValue || ''}
                        onValueChange={(value) => {
                          if (value === 'tenant_external' || value === 'tenant_3pl') {
                            setLocalCRMValue(value);
                            handleCRMChange(value);
                          }
                        }}
                        disabled={isProcessingCRM || updateCRMSettingsMutation.isPending}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="tenant_external" id="tenant_external" />
                          <Label htmlFor="tenant_external" className="font-normal cursor-pointer">
                            {t('crmManagement.options.tenant_external')}
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="tenant_3pl" id="tenant_3pl" />
                          <Label htmlFor="tenant_3pl" className="font-normal cursor-pointer">
                            {t('crmManagement.options.tenant_3pl')}
                          </Label>
                        </div>
                      </RadioGroup>
                      {/* Modal de confirmación para cambio de CRM */}
                      <ConfirmModal ref={crmChangeModalRef} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Historial de Tarifas */}
            <div className="space-y-4 mb-6 pb-6 border-b border-card-border">
              <div className="mb-4">
                <h2 className="font-semibold text-lg mb-1">{t('rateAssignments.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('rateAssignments.description')}</p>
              </div>
              <RateAssignmentsTab teamId={clientId} />
            </div>

            {/* Tiendas */}
            <div className="space-y-4 pt-4">
              <div className="mb-4">
                <h2 className="font-semibold text-lg mb-1">{t('tabs.shops.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('tabs.shops.description')}</p>
              </div>
              <ShopsTab teamId={clientId} />
            </div>
          </TabsContent>

          {/* Tab 4: Facturación - Config + Facturas + Cuentas bancarias */}
          <TabsContent value="billing">
            {/* Configuración de Facturación */}
            {beeping_team && (
              <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
                <div className="col-span-1 md:col-span-2">
                  <h2 className="font-semibold text-lg mb-1">{t('sections.billingConfig')}</h2>
                  <p className="text-sm text-muted-foreground">{t('sections.billingConfigDescription')}</p>
                </div>
                <div className="col-span-1 md:col-span-4">
                  {canEditBilling && (
                    <div className="flex justify-end mb-4">
                      <Button variant="outline" size="sm" onClick={() => setIsEditBillingModalOpen(true)}>
                        <FontAwesomeIcon icon={faPencil} className="w-4 h-4 mr-2" />
                        {t('editBilling.button')}
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.billingFrequency')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.billing_frequency
                          ? t(`billingFrequencies.${beeping_team.billing_frequency}`)
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.minimumMonthlyFee')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.minimum_monthly_fee !== null && beeping_team.minimum_monthly_fee !== undefined
                          ? formatCurrency(beeping_team.minimum_monthly_fee)
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <p className="text-sm font-medium">{t('fields.minimumMonthlyFeeGraceMonths')}</p>
                        <InfoTooltip
                          text={t('fields.minimumMonthlyFeeGraceMonthsTooltip')}
                          dataTestId="minimum-monthly-fee-grace-months-tooltip"
                          maxWidth="md"
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.minimum_monthly_fee_grace_months !== null &&
                        beeping_team.minimum_monthly_fee_grace_months !== undefined
                          ? `${beeping_team.minimum_monthly_fee_grace_months} ${t('fields.months')}`
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.invoiceExpiration')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.invoice_expiration !== null && beeping_team.invoice_expiration !== undefined
                          ? beeping_team.invoice_expiration
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.codAdvanceDaysAfterDelivery')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.cod_advance_days_after_delivery !== null &&
                        beeping_team.cod_advance_days_after_delivery !== undefined
                          ? beeping_team.cod_advance_days_after_delivery
                          : t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.holdedId')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.holded_id || t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.holdedClientAccount')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.holded_client_account || t('placeholders.noData')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.invoiceWalletCompensation')}</p>
                      <Badge variant={beeping_team.invoice_wallet_compensation ? 'default' : 'secondary'}>
                        {beeping_team.invoice_wallet_compensation ? t('booleanValues.yes') : t('booleanValues.no')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.walletVisible')}</p>
                      <Badge variant={beeping_team.wallet_visible ? 'default' : 'secondary'}>
                        {beeping_team.wallet_visible ? t('booleanValues.yes') : t('booleanValues.no')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.taxes')}</p>
                      <p className="text-sm text-muted-foreground">
                        {beeping_team.taxes && Array.isArray(beeping_team.taxes) && beeping_team.taxes.length > 0
                          ? beeping_team.taxes.join(', ')
                          : t('placeholders.noData')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de edición de facturación */}
            {beeping_team && (
              <EditBillingModal
                open={isEditBillingModalOpen}
                onOpenChange={setIsEditBillingModalOpen}
                teamId={clientId}
                initialData={{
                  invoice_wallet_compensation: beeping_team.invoice_wallet_compensation ?? false,
                  wallet_visible: beeping_team.wallet_visible ?? false,
                  holded_id: beeping_team.holded_id ?? '',
                  holded_client_account: beeping_team.holded_client_account ?? '',
                  taxes: Array.isArray(beeping_team.taxes)
                    ? beeping_team.taxes
                    : beeping_team.taxes
                      ? [String(beeping_team.taxes)]
                      : [],
                  billing_frequency: beeping_team.billing_frequency ?? 'monthly',
                  cod_advance_days_after_delivery: beeping_team.cod_advance_days_after_delivery ?? 0,
                  invoice_expiration: beeping_team.invoice_expiration ?? 0,
                  minimum_monthly_fee: beeping_team.minimum_monthly_fee ?? null,
                  minimum_monthly_fee_grace_months: beeping_team.minimum_monthly_fee_grace_months ?? null,
                }}
                onSuccess={() => refetch()}
              />
            )}

            {/* Facturas */}
            <div className="space-y-4 mb-6 pb-6 border-b border-card-border">
              <div className="mb-4">
                <h2 className="font-semibold text-lg mb-1">{t('tabs.invoices.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('tabs.invoices.description')}</p>
              </div>
              <InvoicesTab teamId={clientId} />
            </div>

            {/* Cuentas Bancarias */}
            <div className="space-y-4 pt-4">
              <div className="mb-4">
                <h2 className="font-semibold text-lg mb-1">{t('tabs.bankAccounts.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('tabs.bankAccounts.description')}</p>
              </div>
              <BankAccountsTab teamId={clientId} />
            </div>
          </TabsContent>

          {/* Tab 5: Migración */}
          <TabsContent value="migration">
            <MigrationTab teamId={clientId} clientName={client.name} />
          </TabsContent>

          {/* Tab 6: Contratos */}
          <TabsContent value="contracts">
            {client.contract_template ? (
              <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4">
                <div className="col-span-1 md:col-span-2">
                  <h2 className="font-semibold text-lg mb-1">{t('sections.contractTemplate')}</h2>
                  <p className="text-sm text-muted-foreground">{t('sections.contractTemplateDescription')}</p>
                </div>
                <div className="col-span-1 md:col-span-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-1">{t('fields.contractTemplateId')}</p>
                      <p className="text-sm text-muted-foreground">
                        {client.contract_template.name || client.contract_template.id}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FontAwesomeIcon icon={faFileContract} className="w-12 h-12 mb-4 opacity-50" />
                <p>{t('tabs.contracts.empty')}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
